import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { PUBLIC_APP_NAME } from '../config/publicBrand';

export type WorkspaceBranding = {
  companyName?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  brandColor?: string;
  pdfVariant?: 'premium' | 'generic';
  pdfTemplate?: string;
  useBundledAssociationBadges?: boolean;
};

type BrandingContextValue = {
  branding: WorkspaceBranding | null;
  loading: boolean;
  /** Company display name when logged in, else public fallback */
  appName: string;
  /** Footer contact line built from branding */
  footerContactLine: string;
  refreshBranding: () => Promise<void>;
};

const BrandingContext = createContext<BrandingContextValue | undefined>(undefined);

export function BrandingProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { user, loading: authLoading } = useAuth();
  const [branding, setBranding] = useState<WorkspaceBranding | null>(null);
  const [loading, setLoading] = useState(false);

  const refreshBranding = useCallback(async () => {
    if (!user) {
      setBranding(null);
      return;
    }
    setLoading(true);
    try {
      const res = await apiRequest<{ branding: WorkspaceBranding | null }>('/api/branding?summary=1');
      setBranding(res.branding ?? null);
    } catch {
      setBranding(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setBranding(null);
      return;
    }
    void refreshBranding();
  }, [user, authLoading, refreshBranding]);

  const appName = useMemo(() => {
    const cn = (branding?.companyName || '').trim();
    if (cn) return cn;
    return PUBLIC_APP_NAME;
  }, [branding?.companyName]);

  const footerContactLine = useMemo(() => {
    const parts = [branding?.address, branding?.phone, branding?.email]
      .map((x) => (typeof x === 'string' ? x.trim() : ''))
      .filter(Boolean);
    return parts.join(' • ');
  }, [branding?.address, branding?.phone, branding?.email]);

  const value = useMemo(
    () => ({ branding, loading, appName, footerContactLine, refreshBranding }),
    [branding, loading, appName, footerContactLine, refreshBranding]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useWorkspaceBranding(): BrandingContextValue {
  const ctx = useContext(BrandingContext);
  if (!ctx) {
    throw new Error('useWorkspaceBranding must be used within BrandingProvider');
  }
  return ctx;
}
